import { createContext, useContext, useState } from "react";
import { ModalContextValue, ModalProviderProps } from "./types";
import { request } from "../../services";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import checkBefore from '../../helpers/selectionOfActions';
import message from "antd/lib/message";
import { useLocation } from "react-router-dom";
import { TranslationContext } from "../../contexts/translation/index.context";

const SignModalContext = createContext<ModalContextValue | undefined>(
  undefined
);

export function SignModalProvider({ children }: ModalProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<number[]>([]);
  const [token, setToken] = useState("");
  const location = useLocation();
  const { intl } = useContext(TranslationContext);



  const addFile = (fileId: number) => {
    setSelectedFiles((prev) => [...prev, fileId]);
  };

  const onChange = (id: number) => (e: CheckboxChangeEvent) => {
    setSelectedFiles((prev) =>
      e.target.checked ? [...prev, id] : prev.filter((fileId) => fileId !== id)
    );
  };

  const handleSelectAll = (files: { id: number }[]) => {
    setSelectedFiles(files.map((file) => file.id));
  };

  const clearFiles = () => {
    setSelectedFiles([]);
  };

  const openSignModal = async (sessionData: any, pathname: string) => {
    
  //Компонент перевірка на доступ до внесення даних чи змін
  let accessRights = await checkBefore(location.pathname, undefined, 'sign-file');
    if (!accessRights) {
      message.error(intl("you-do-not-have-access-rights"));
      return;
    }

    setIsOpen(true);
    try {
      const getTokenResponse = await request(
        `${pathname.split("/").slice(0, 2).join("/")}/gettoken`,
        {
          method: "post",
          body: {
            id: sessionData?.bta_documents_id,
            page: pathname.split("/").pop(),
          },
          cancelTokenKey: "gettoken",
        }
      );
      
      if (!getTokenResponse.success) {
        throw new Error("Failed to fetch token");
      }
      
      setToken(getTokenResponse.content);
      
      await request("/sign/sign-files", {
        method: "post",
        body: {
          id: sessionData?.bta_documents_id,
          page: pathname.split("/").pop(),
          filesId: selectedFiles.join(", "),
        },
        cancelTokenKey: "signfiles",
      });
    } catch (error) {
      console.error("Error in openSignModal:", error);
    }
  };

  const closeSignModal = () => {
    setIsOpen(false);
    setToken("");
    clearFiles();
  };

  return (
    <SignModalContext.Provider
      value={{
        isOpen,
        openSignModal,
        closeSignModal,
        selectedFiles,
        setSelectedFiles,
        onChange,
        handleSelectAll,
        clearFiles,
        token,
      }}
    >
      {children}
    </SignModalContext.Provider>
  );
}

export const useSignModal = () => {
  const context = useContext(SignModalContext);
  if (!context) {
    throw new Error("useSignModal must be used within a SignModalProvider");
  }
  return context;
};
